import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Briefcase, Building2, ClipboardList, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/recruiting/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/constants";

export default function MyApplications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/applications/my").then((res) => setApps(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <Skeleton className="h-28 rounded-xl" />
        <Skeleton className="h-28 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Hi, {user.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground mt-1 text-sm">Track all your applications in one place.</p>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-semibold text-lg">My applications ({apps.length})</h2>
          <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={() => navigate("/jobs")} data-testid="browse-more-jobs-button">
            <Briefcase className="h-4 w-4 mr-2" /> Find jobs
          </Button>
        </div>

        {apps.length === 0 ? (
          <Card className="rounded-xl border-border shadow-none">
            <EmptyState
              icon={ClipboardList}
              title="No applications yet"
              description="Browse open roles and apply with just your mobile number."
              actionLabel="Find Jobs"
              onAction={() => navigate("/jobs")}
              testId="member-applications-empty"
            />
          </Card>
        ) : (
          <div className="space-y-3" data-testid="member-applications-list">
            {apps.map((app) => (
              <Link key={app.id} to={`/member/applications/${app.id}`} className="block" data-testid="member-application-card">
                <Card className="rounded-xl border-border shadow-none hover:shadow-[var(--shadow-sm)] hover:border-primary/40 transition-all">
                  <CardContent className="p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-heading font-semibold leading-tight">{app.job?.title}</p>
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{app.job?.hiring_brand}</span>
                          <span>· {app.job?.location}</span>
                          <span>· Applied {formatDate(app.created_at)}</span>
                          <span className="font-mono">· {app.code}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={app.status} />
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Card className="rounded-xl border-border shadow-none">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <span className="h-11 w-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
            <FileText className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <p className="font-medium text-sm">Free Resume Builder</p>
            <p className="text-xs text-muted-foreground mt-0.5">Create a professional resume with live preview and PDF download — completely free.</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-lg bg-white shrink-0" onClick={() => navigate("/member/resume")} data-testid="open-resume-builder-button">
            Open Resume Builder
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
